import * as React from 'react';
import { withTheme, WithThemeProps } from '../theme';
import { Outbound } from './Outbound/Outbound';
import { addDays, toDate } from 'date-fns';
import { Matrix } from './Matrix/Matrix';
import { createDatesArray } from '../utils';
import { MoneyType, PriceMap, PriceMatrixProps, PriceRT } from '../types';

export enum HeaderType {
	Outbound,
	Return
}

interface State {
	[HeaderType.Outbound]: Date;
	[HeaderType.Return]: Date;
}

const memoize = require('memoize-one');

@withTheme
export class PriceMatrix extends React.Component<Partial<WithThemeProps> & PriceMatrixProps, State> {
	public static displayName = 'PriceMatrix';
	private minOutboundDate: number = Infinity;
	private maxOutboundDate: number = 0;
	private minReturnDate: number = Infinity;
	private maxReturnDate: number = 0;
	private readonly minPrice: MoneyType = {
		amount: Infinity,
		currency: null
	};
	private readonly maxPrice: MoneyType = {
		amount: 0,
		currency: null
	};

	public memoizedFill = typeof memoize === 'function' ? memoize(this.fill) : memoize.default(this.fill);

	public state: State = {
		[HeaderType.Outbound]: null,
		[HeaderType.Return]: null
	};

	constructor(props: Partial<WithThemeProps> & PriceMatrixProps) {
		super(props);

		this.getDatesRange = this.getDatesRange.bind(this);
		this.arrowHandler = this.arrowHandler.bind(this);
	}

	public getDatesRange(type: HeaderType) {
		const minDateFromState = this.state[type],
			minDateFromClass = type === HeaderType.Outbound ? this.minOutboundDate : this.minReturnDate,
			minDate = minDateFromState ? minDateFromState : toDate(minDateFromClass);

		return {
			minDate: minDate,
			maxDate: addDays(minDate, this.props.size - 1)
		};
	}

	public getDatesMatrix() {
		const outboundDatesRange = this.getDatesRange(HeaderType.Outbound),
			returnDatesRange = this.getDatesRange(HeaderType.Return),
			outboundDatesArray = createDatesArray(outboundDatesRange.minDate, outboundDatesRange.maxDate),
			returnDatesArray = createDatesArray(returnDatesRange.minDate, returnDatesRange.maxDate);

		return outboundDatesArray.map(date => {
			return {
				outboundDate: date,
				returnDates: returnDatesArray
			};
		});
	}

	public checkDatesRange(type: HeaderType, startDate: Date): boolean {
		const minDate = type === HeaderType.Outbound ? this.minOutboundDate : this.minReturnDate,
			maxDate = type === HeaderType.Outbound ? this.maxOutboundDate : this.maxReturnDate,
			startDateTimestamp = startDate.getTime(),
			endDateTimestamp = addDays(startDate, this.props.size - 1).getTime();

		return startDateTimestamp >= minDate && endDateTimestamp <= maxDate;
	}

	public arrowHandler(type: HeaderType, direction: 'next' | 'prev') {
		const datesRange = this.getDatesRange(type),
			newStartDate = addDays(datesRange.minDate, direction === 'next' ? 1 : -1);

		this.checkDatesRange(type, newStartDate) &&
			this.setState(() => {
				if (this.props.onRangeChanges) {
					this.props.onRangeChanges(
						type === HeaderType.Outbound ? 'vertical' : 'horizontal',
						newStartDate,
						addDays(newStartDate, this.props.size - 1)
					);
				}

				return {
					[type]: newStartDate
				};
			});
	}

	public fill(prices: PriceRT[]): PriceMap {
		const pricesMap = {};

		prices.map(price => {
			const returnTimestamp = price.dateBack.getTime(),
				outboundTimestamp = price.dateTo.getTime();

			if (returnTimestamp > this.maxReturnDate) {
				this.maxReturnDate = returnTimestamp;
			}

			if (returnTimestamp < this.minReturnDate) {
				this.minReturnDate = returnTimestamp;
			}

			if (outboundTimestamp > this.maxOutboundDate) {
				this.maxOutboundDate = outboundTimestamp;
			}

			if (outboundTimestamp < this.minOutboundDate) {
				this.minOutboundDate = outboundTimestamp;
			}

			if (price.price && price.price.amount < this.minPrice.amount) {
				this.minPrice.amount = price.price.amount;
			}

			if (price.price && price.price.amount > this.maxPrice.amount) {
				this.maxPrice.amount = price.price.amount;
			}

			pricesMap[`${price.dateTo.getTime()}-${price.dateBack.getTime()}`] = {
				money: price.price,
				isLoading: price.isLoading
			};
		});

		return pricesMap;
	}

	public render(): React.ReactNode {
		const pricesMap = this.memoizedFill(this.props.prices),
			styles = this.props.theme.PriceMatrixStyles;

		return (
			<div className={styles.container}>
				<Matrix
					dates={this.getDatesMatrix()}
					prices={pricesMap}
					minPrice={this.minPrice}
					maxPrice={this.maxPrice}
					onDateSelect={this.props.onDateSelect}
					verticalHeader={ref => (
						<Outbound
							{...this.getDatesRange(HeaderType.Outbound)}
							type={HeaderType.Outbound}
							onArrowClick={this.arrowHandler}
							innerRef={ref}
						/>
					)}
					horizontalHeader={ref => (
						<Outbound
							{...this.getDatesRange(HeaderType.Return)}
							type={HeaderType.Return}
							onArrowClick={this.arrowHandler}
							innerRef={ref}
						/>
					)}
				/>
			</div>
		);
	}
}

export default PriceMatrix;
